import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

// Third-party
import { useAtom, useAtomValue } from 'jotai';

// Hooks
import { useGroupConversation } from '@/utils/hooks/swr/group/useGroupConversations';
import useComments from '@/utils/hooks/swr/useComments';
import useMentions from '@/utils/hooks/swr/useMentions';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
// Atoms
import {
    messageFilterSecondaryAtom,
    openedMessageAtom
} from '@/components/pages/conversations/SocialInbox/atoms';

// Types
import type { SocialInboxMessage } from '@/components/pages/conversations/SocialInbox/types';

// Lib/Constants
import { MESSAGE_FILTER_META } from '@/components/pages/conversations/SocialInbox/const';
import {
    MANAGE_IMPORTANT_TRACKER,
    MANAGE_MENTIONS_TRACKER,
    MANAGE_RESOLVED_TRACKER
} from '@/lib/routes/privateRoutes';
import useActiveSocialProfile from '../user/useActiveSocialProfile';

type MessageMutateFunction = (prevMessages: SocialInboxMessage[]) => SocialInboxMessage[];
type MessagesInput = SocialInboxMessage[] | MessageMutateFunction;

const sortMessages = (messages: SocialInboxMessage[]) => {
    return messages.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
};

const resolveParams = (path: string, fetchingFromGroup: boolean) => {
    if ([MANAGE_IMPORTANT_TRACKER, MANAGE_RESOLVED_TRACKER].includes(path)) {
        const socialInboxType = path.replace('/conversations/', '');
        return fetchingFromGroup
            ? { [`is_${MESSAGE_FILTER_META[socialInboxType].status}`]: true }
            : { status: MESSAGE_FILTER_META[socialInboxType].status };
    }
    return null;
};

type DataSource = {
    messages: SocialInboxMessage[];
    isLoading: boolean;
    isLoadingMore: boolean;
    isLoadMore: boolean;
    loadMore: () => Promise<any[]>;
    mutate: any; // You might want to type this properly based on your needs
};

const useCommentsDataSource = (
    activeSocialProfileId: number | null,
    shouldFetch: boolean,
    path: string
): DataSource => {
    const prevPathRef = useRef(path);
    const resolvedParams = resolveParams(path, false);

    const {
        dataComments: socialProfileComments,
        isLoadingInitialData: isLoading,
        isLoadingMore,
        isLoadMore,
        loadMoreComments: loadMore,
        mutateComments: mutate,
        setRequestParams
    } = useComments({
        activeSPID: activeSocialProfileId,
        shouldFetch,
        params: resolvedParams,
        options: {
            revalidateOnFocus: shouldFetch,
            revalidateOnReconnect: shouldFetch,
            dedupingInterval: 2000
        }
    } as any);

    // Only update params when path changes, not on every render
    useEffect(() => {
        if (prevPathRef.current !== path) {
            prevPathRef.current = path;
            setRequestParams(resolvedParams);
        }
    }, [path, setRequestParams, resolvedParams]);

    return {
        messages: socialProfileComments?.flatMap((page) => page.data) ?? [],
        isLoading,
        isLoadingMore,
        isLoadMore,
        loadMore,
        mutate
    };
};

const useMentionsDataSource = (
    activeSocialProfileId: number | null,
    shouldFetch: boolean
): DataSource => {
    const {
        isLoadingInitialData: isLoading,
        isLoadingMore,
        isMoreToBeLoaded: isLoadMore,
        loadMore,
        mentions,
        mutate
    } = useMentions({
        activeSPID: activeSocialProfileId,
        limit: 10,
        shouldFetch,
        options: {
            revalidateOnFocus: shouldFetch,
            revalidateOnReconnect: shouldFetch
        }
    });

    const messages = mentions;

    return {
        messages,
        isLoading,
        isLoadingMore,
        isLoadMore,
        loadMore,
        mutate
    };
};

const useGroupDataSource = (shouldFetch: boolean, path: string): DataSource => {
    const {
        data: messages,
        isLoading,
        isLoadingMore,
        isLoadMore,
        loadMore,
        mutate
    } = useGroupConversation({
        shouldFetch,
        params: resolveParams(path, true),
        options: {
            revalidateOnFocus: shouldFetch,
            revalidateOnReconnect: shouldFetch
        }
    });

    return {
        messages: messages ?? [],
        isLoading,
        isLoadingMore,
        isLoadMore,
        loadMore,
        mutate
    };
};

/**
 * @description Hook to fetch comments, mentions and group conversations from a social profile or a group
 * TODO: Rename this hook for better readability
 * ex.: useSocialInboxDataSource
 *
 */
export const useMessages = () => {
    const { activeGroup } = useActiveGroup();
    const { activeSocialProfile } = useActiveSocialProfile();
    const [openedMessage, setOpenedMessage] = useAtom(openedMessageAtom);
    const filterState = useAtomValue(messageFilterSecondaryAtom);
    const path = usePathname();

    const isGroupConversation = !activeSocialProfile && activeGroup !== null;
    const isMentions = path === MANAGE_MENTIONS_TRACKER;
    const isConversation = !isGroupConversation && path !== MANAGE_MENTIONS_TRACKER;

    // Initialize all data sources
    const commentsSource = useCommentsDataSource(activeSocialProfile?.id, isConversation, path);
    const mentionsSource = useMentionsDataSource(activeSocialProfile?.id, isMentions);
    const groupSource = useGroupDataSource(isGroupConversation, path);

    // Determine active data source
    const activeSource = (() => {
        if (isGroupConversation) return groupSource;
        if (isMentions) return mentionsSource;
        return commentsSource;
    })();

    const messages = sortMessages(activeSource.messages);

    useEffect(() => {
        if (openedMessage) {
            const updatedMessage = messages.find((message) => message.id === openedMessage.id);
            setOpenedMessage(updatedMessage);
        }
    }, [messages, openedMessage, setOpenedMessage]);

    useEffect(() => {
        if (openedMessage) {
            const updatedMessage = messages.find((message) => message.id === openedMessage.id);

            if (updatedMessage) {
                setOpenedMessage(updatedMessage);
            }
        }
    }, [messages]);

    const handleMutateMessages = (newMessages: MessagesInput) => {
        const getUpdatedMessages = (currentMessages: SocialInboxMessage[]): SocialInboxMessage[] =>
            typeof newMessages === 'function' ? newMessages(currentMessages) : newMessages;

        const updatedMessages = getUpdatedMessages(messages);
        const dataKey = isGroupConversation ? 'comments' : 'data';

        activeSource.mutate((prev: any[]) =>
            prev.map((page) => ({
                ...page,
                [dataKey]: page[dataKey].map((data: SocialInboxMessage) => {
                    const updatedItem = updatedMessages.find((item) => item.id === data.id);
                    return updatedItem ? { ...data, ...updatedItem } : data;
                })
            }))
        );
    };

    const filteredMessages =
        filterState.filter === 'all' ? messages : messages.filter((message) => !message.is_read);

    /**
     * Function to manually refresh the current data source
     * Used by the refresh button in the UI
     */
    const refreshMessages = async () => {
        return activeSource.mutate(undefined, { revalidate: true });
    };

    return {
        isLoading: activeSource.isLoading,
        isLoadingMore: activeSource.isLoadingMore,
        isLoadMore: activeSource.isLoadMore,
        loadMore: activeSource.loadMore,
        messages,
        filteredMessages,
        mutateMessages: handleMutateMessages,
        refreshMessages
    };
};
